// src/components/Usuarios/UsersIndex.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PageContainer,
  HeaderContainer,
  FormContainer,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  AddUserButton,
  GridContainer,
  DevExtremeModalGlobalStyles,
} from "./Users.styles";

import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  Pager,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, {
  SimpleItem,
  Label as DxLabel,
  RequiredRule,
} from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { apiService } from "../../services/apiService";
import { userService, UsuariosFilterParams } from "../../services/userService";
import { catalogoService } from "../../services/catalogoService";
import { ApiError } from "../../services/apiError";
import { Users } from "../../dtos/User";
import { useNavigate } from "react-router-dom";
import ChangePasswordPopup from "./ChangePasswordPopup";

/** RF-02 / RNF-01: determina si el usuario en sesión tiene rol Administrador. */
const currentUserIsAdmin = (): boolean => {
  try {
    const stored = localStorage.getItem("userData");
    if (!stored) return false;
    const rol = JSON.parse(stored)?.user?.rol;
    return (
      rol === 0 ||
      rol === "0" ||
      (typeof rol === "string" && rol.toLowerCase() === "admin")
    );
  } catch {
    return false;
  }
};

/* ========================= helpers ========================= */
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

const UsersList: React.FC = () => {
  const [dataSource, setDataSource] = useState<Users[]>([]);
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const dataGridRef = useRef<any>(null);
  const [editingItem, setEditingItem] = useState<Partial<Users> | null>(null);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Users | null>(null);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const formRef = useRef<FormInstance | null>(null);

  /* ===== RF-02: cambio de contraseña (solo Admin) ===== */
  const isAdmin = useMemo(() => currentUserIsAdmin(), []);
  const [isPasswordPopupVisible, setIsPasswordPopupVisible] = useState(false);
  const [passwordUser, setPasswordUser] = useState<Partial<Users> | null>(null);

  /* ===== Filtros (por imagen: nomina, nombre, correo, sucursalId, activo) ===== */
  const [filterNomina, setFilterNomina] = useState<string>("");
  const [filterNombre, setFilterNombre] = useState<string>("");
  const [filterCorreo, setFilterCorreo] = useState<string>("");
  const [filterSucursal, setFilterSucursal] = useState<number | "">("");
  const [filterActivo, setFilterActivo] = useState<"" | "true" | "false">("");
  const navigate = useNavigate();
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Usuarios");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Usuarios.xlsx",
    );
  };
  const rolesDisponibles = useMemo(
    () => [
      { id: 0, rol: "Admin" },
      { id: 1, rol: "Operador" },
    ],
    [],
  );

  /* ===== Carga inicial: catálogo sucursales + sucursal por defecto + 1ra consulta ===== */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [branches] = await Promise.all([catalogoService.getBranch()]);
        if (branches.error) throw new Error(branches.error.message);
        setSucursales(branches.data || []);

        // sucursal del usuario logueado como valor por defecto
        let sucursalId: number | undefined;
        try {
          const stored = localStorage.getItem("userData");
          if (stored) sucursalId = Number(JSON.parse(stored)?.user?.sucursalId);
          if (sucursalId != null && !Number.isNaN(sucursalId)) {
            setFilterSucursal(sucursalId);
          }
        } catch {
          /* ignore */
        }

        await fetchUsers({ sucursalId });
      } catch (err: any) {
        notify({ message: err?.message || "Error al iniciar." }, "error", 3500);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Petición con filtros actuales ===== */
  const fetchUsers = async (override?: Partial<UsuariosFilterParams>) => {
    setLoading(true);
    try {
      const params: UsuariosFilterParams = {
        nomina: (filterNomina || "").trim() || undefined,
        nombre: (filterNombre || "").trim() || undefined,
        correo: (filterCorreo || "").trim() || undefined,
        sucursalId: filterSucursal === "" ? undefined : Number(filterSucursal),
        activo: filterActivo === "" ? undefined : filterActivo === "true",
        ...override,
      };

      const resp = await userService.getFiltered(params);
      if (resp.error) throw new Error(resp.error.message);
      setDataSource(resp.data ?? []);
    } catch (err: any) {
      const apiError = handleApiError(err);
      setDataSource([]);
      notify(apiError.message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  /* ===== Buscar ===== */
  const handleSearch = () => fetchUsers();

  /* ===== CRUD ===== */
  const handleAdd = () => {
    setEditingItem({ activo: true, rol: 1 });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: Users) => {
    const itemToEdit = { ...data };
    delete (itemToEdit as any).password; // no mostrarla
    setEditingItem(itemToEdit);
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: Users) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  /* RF-02: abre el flujo de cambio de contraseña para el usuario en edición. */
  const handleOpenChangePassword = () => {
    const current = (formRef.current as any)?.option("formData") ?? editingItem;
    if ((current as any)?.is365) {
      notify(
        "Los usuarios Microsoft 365 no tienen contraseña local.",
        "info",
        3000,
      );
      return;
    }
    setPasswordUser(current as Partial<Users>);
    setIsPasswordPopupVisible(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await apiService.patch(
        `/api/Usuarios/cambiastatus/${itemToDelete.nomina}`,
        "",
      );
      notify("Cambio de estatus realizado con éxito.", "success", 2000);
      await fetchUsers();
    } catch {
      notify("Error al cambiar el estatus del registro.", "error", 3000);
    } finally {
      setIsDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const checkPasswordComplexity = (password: string | undefined) => {
    if (!password)
      return { isValid: false, message: "La contraseña es obligatoria." };
    if (password.length < 8)
      return {
        isValid: false,
        message: "La contraseña debe tener al menos 8 caracteres.",
      };
    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&._-])/;
    if (!pattern.test(password))
      return {
        isValid: false,
        message:
          "Debe incluir mayúsculas, minúsculas, números y caracter especial.",
      };
    return { isValid: true, message: null };
  };

  const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;

      if (responseData?.data && typeof responseData.data === "string") {
        return responseData.data;
      }

      if (responseData?.message && typeof responseData.message === "string") {
        return responseData.message;
      }

      if (typeof responseData === "string") {
        return responseData;
      }

      return "Ocurrió un error en el servidor.";
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return "Ocurrió un error inesperado. Revisa tu conexión de red.";
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const formValidation = await (form as any).validate();
    if (!formValidation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = (form as any).option("formData") as Users;

    if (!formData.id) {
      const passwordCheck = checkPasswordComplexity((formData as any).password);
      if (!passwordCheck.isValid) {
        notify(passwordCheck.message!, "error", 3000);
        return;
      }
    }

    try {
      if (formData.id) {
        // RF-02 / RNF-01: la contraseña NO se envía en la actualización general;
        // el cambio de contraseña es exclusivo del flujo "Cambiar contraseña".
        const { password, ...userData } = formData as any;
        await apiService.put<Users, Users>(
          `/api/Usuarios/${formData.id}`,
          userData,
        );
        notify("Usuario actualizado con éxito.", "success", 2000);
      } else {
        const response = await apiService.post<Users, Users>("/api/Usuarios", {
          ...formData,
          is365: !!(formData as any).is365,
        } as any);

        console.log("Respuesta al crear usuario:", response);
        notify("Registro creado con éxito.", "success", 2000);
      }

      setIsFormPopupVisible(false);
      await fetchUsers();
    } catch (error) {
      notify(getErrorMessage(error), "info", 3000);
    }
  };

  /* ===== celdas ===== */
  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as Users;
    const isActive = item.activo;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(item)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <PageContainer>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <DevExtremeModalGlobalStyles />
      <HeaderContainer>Catálogo de Usuarios</HeaderContainer>

      {/* ===== Panel de filtros (nomina, nombre, correo, sucursal, activo) ===== */}
      <FilterBar>
        <Field>
          <Label>Nómina</Label>
          <StyledInput
            placeholder="Ej. A12345"
            value={filterNomina}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterNomina(e.target.value)
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Field>

        <Field>
          <Label>Nombre</Label>
          <StyledInput
            placeholder="Ej. Juan Pérez"
            value={filterNombre}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterNombre(e.target.value)
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Field>

        <Field>
          <Label>Correo</Label>
          <StyledInput
            placeholder="ejemplo@infra.com"
            value={filterCorreo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterCorreo(e.target.value)
            }
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Field>

        <Field>
          <Label>Sucursal</Label>
          <StyledSelect
            value={filterSucursal}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilterSucursal(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
          >
            <option value="">Todas</option>
            {sucursales.map((s) => {
              const id = s.id ?? s.sucursalId ?? s.value ?? "";
              const num = s.numSucursal ?? s.cc ?? s.numero ?? "";
              const name = s.nombreSuc ?? s.nombre ?? s.name ?? "";
              const label =
                [num, name].filter(Boolean).join(" - ") || String(id);
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              );
            })}
          </StyledSelect>
        </Field>

        <Field>
          <Label>Activo</Label>
          <StyledSelect
            value={filterActivo}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setFilterActivo(e.target.value as "" | "true" | "false")
            }
          >
            <option value="">--</option>
            <option value="true">Activo</option>
            <option value="false">Inactivo</option>
          </StyledSelect>
        </Field>

        <AddUserButton onClick={handleSearch}>Buscar</AddUserButton>
      </FilterBar>

      <GridContainer>
        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr="id"
          wordWrapEnabled
          showBorders
          columnAutoWidth
          hoverStateEnabled
          loadPanel={{ enabled: loading }}
        >
          <Paging defaultPageSize={10} />
          <HeaderFilter visible />

          <Toolbar>
            <Item name="searchPanel" />
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Exportar Excel",
                icon: "xlsxfile",
                stylingMode: "contained",
                type: "default",
                onClick: handleExportExcel,
              }}
            />
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Añadir Usuario",
                icon: "add",
                stylingMode: "contained",
                type: "default",
                onClick: handleAdd,
              }}
            />
          </Toolbar>
          <SearchPanel
            visible={true}
            highlightCaseSensitive={true}
            placeholder="Buscar..."
          />
          <Column
            caption="CC - Sucursal"
            allowSorting={true}
            allowFiltering={true}
            allowHeaderFiltering={true}
            calculateCellValue={(row: Users) => {
              const nom = row?.nombreSuc ?? "";
              const num = row?.numSucursal ?? "";
              if (!nom && !num) return "";
              if (!nom) return String(num);
              if (!num) return nom;
              return `${num} - ${nom}`;
            }}
          />
          <Column dataField="nombre" caption="Nombre" />
          <Column dataField="nomina" caption="Nómina" />
          <Column dataField="correo" caption="Correo" />
          <Column dataField="rol" caption="Rol" />
          <Column dataField="password" visible={false} />
          <Column dataField="is365" visible={false} />
          <Column
            dataField="activo"
            caption="Estatus"
            alignment="center"
            cellRender={EstatusCell}
          />
          <Column
            caption="Acciones"
            alignment="center"
            width={120}
            allowExporting={false}
            cellRender={ActionsCell}
          />
          <FilterRow visible />
          <HeaderFilter visible />
          <Paging defaultPageSize={50} />
          <Pager
            visible={false}
            allowedPageSizes={[50]}
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
        </DataGrid>
      </GridContainer>

      {/* Popup de Edición / Creación */}
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton
        showTitle
        title={
          editingItem && editingItem.id ? "Editar Usuario" : "Añadir Usuario"
        }
        width={500}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Guardar",
              type: "success",
              stylingMode: "contained",
              onClick: handleSave,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              type: "default",
              stylingMode: "text",
              onClick: () => setIsFormPopupVisible(false),
            },
          },
        ]}
      >
        <FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => (formRef.current = e.component ?? null)}
          >
            <SimpleItem dataField="nombre">
              <DxLabel text="Nombre" />
              <RequiredRule message="El nombre es requerido." />
            </SimpleItem>
            <SimpleItem dataField="nomina">
              <DxLabel text="Nomina" />
              <RequiredRule message="La nomina es requerida." />
            </SimpleItem>
            <SimpleItem dataField="correo">
              <DxLabel text="Correo" />
              <RequiredRule message="El correo es obligatorio" />
            </SimpleItem>
            <SimpleItem
              dataField="rol"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: rolesDisponibles,
                valueExpr: "id",
                displayExpr: "rol",
                placeholder: "Seleccione",
              }}
            >
              <DxLabel text="Rol" />
              <RequiredRule message="Debes seleccionar un rol." />
            </SimpleItem>
            {!editingItem?.id && (
              <SimpleItem
                dataField="password"
                editorOptions={{ mode: "password" }}
              >
                <DxLabel text="Contraseña" />
                <RequiredRule message="La contraseña es requerida." />
              </SimpleItem>
            )}
            <SimpleItem
              dataField="sucursalId"
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: sucursales,
                valueExpr: "id",
                displayExpr: (item: { nombreSuc: any; numSucursal: any }) => {
                  if (!item) return "";
                  return `${item.numSucursal} - ${item.nombreSuc}`;
                },
                placeholder: "Seleccione",
              }}
            >
              <DxLabel text="Sucursal" />
              <RequiredRule message="Debes seleccionar una sucursal." />
            </SimpleItem>
          </Form>

          {/* RF-02: solo visible para Administrador al editar un usuario existente */}
          {isAdmin && editingItem?.id && (
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid #DFE1E6",
              }}
            >
              <Button
                text="Cambiar contraseña"
                icon="key"
                stylingMode="outlined"
                type="default"
                width="100%"
                onClick={handleOpenChangePassword}
              />
            </div>
          )}
        </FormContainer>
      </Popup>

      {/* RF-02 / RF-02.1 / RF-02.2: cambio de contraseña con validación y confirmación */}
      <ChangePasswordPopup
        visible={isPasswordPopupVisible}
        usuario={passwordUser}
        onHiding={() => setIsPasswordPopupVisible(false)}
        onSuccess={() => {
          setIsPasswordPopupVisible(false);
          setIsFormPopupVisible(false);
        }}
      />

      {/* Popup de confirmación */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle
        title={`Confirmar ${
          itemToDelete?.activo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${itemToDelete?.activo ? "Desactivar" : "Activar"}`,
              type: itemToDelete?.activo ? "danger" : "success",
              stylingMode: "contained",
              onClick: async () => {
                await confirmDelete();
              },
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>{itemToDelete?.activo ? "desactivar" : "activar"}</strong>{" "}
            el usuario
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.nombre}"?
          </p>
        </div>
      </Popup>
    </PageContainer>
  );
};

export default UsersList;
